import { registerStyles } from "../utils/registerStyles";
import { useEffect, useRef, useState } from "react";
import { Building2, Save } from "lucide-react";
import { Navigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useCompany } from "../contexts/CompanyContext";
import LoadingIndicator from "../components/LoadingIndicator";
import { MY_COMPANY_ID, saveMyCompany } from "../utils/myCompany";

const EMPTY_FORM = {
  projectName: "", area: "", description: "",
};

export default function MyCompanyPage() {
  const { activeCompany, loading, permissionsLoading, myCompanyEnabled } = useCompany();
  if (loading || permissionsLoading) return <LoadingIndicator message="Laddar Mitt företag..." fullPage />;
  if (!myCompanyEnabled) return <Navigate to="/shifts" replace />;
  if (!activeCompany?.companyAccountUid) return <p>Inget företag valt.</p>;
  return <MyCompanyForm key={activeCompany.companyAccountUid} company={activeCompany} />;
}

function MyCompanyForm({ company }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const busy = useRef(false);
  const companyAccountUid = company.companyAccountUid;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    getDoc(doc(db, "users", companyAccountUid, "projects", MY_COMPANY_ID)).then(projectSnapshot => {
      if (!active) return;
      const project = projectSnapshot.data() || {};
      setForm({
        projectName: project.name || "", area: project.area || "",
        description: project.description || "",
      });
    }).catch(() => {
      if (active) setLoadError("Kunde inte hämta projektet. Försök igen.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [companyAccountUid, attempt]);

  const change = event => {
    const { name, value } = event.target;
    setForm(previous => ({ ...previous, [name]: value }));
    setSaved(false);
    setError("");
  };

  const save = async event => {
    event.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await saveMyCompany(companyAccountUid, form);
      setSaved(true);
    } catch (saveError) {
      setError(saveError.message || "Kunde inte spara projektet. Försök igen.");
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  if (loading) return <LoadingIndicator message="Laddar Mitt företag..." fullPage />;
  if (loadError) return <div role="alert">{loadError} <button onClick={() => setAttempt(value => value + 1)}>Försök igen</button></div>;

  return (
    <div className="my-company-page">
      <p className="my-company-context">
        Visar mitt företag för: <strong>{company.companyName || "Mitt företag"}</strong>
      </p>
      <header className="my-company-heading">
        <span className="my-company-icon"><Building2 size={24} aria-hidden="true" /></span>
        <div><h1>Mitt företag</h1><p>Hantera projektet som används i din schemaläggning.</p></div>
      </header>
      <div className="my-company-layout">
        <form className="my-company-card" onSubmit={save}>
          <div className="my-company-card-heading">
            <h2>Projektuppgifter</h2>
            <p>Ge projektet ett namn som är enkelt att känna igen.</p>
          </div>
          <fieldset disabled={saving} className="my-company-fields" aria-label="Projektuppgifter">
            <div className="my-company-field">
              <label htmlFor="my-company-name">Projektnamn *</label>
              <input id="my-company-name" name="projectName" value={form.projectName} onChange={change} placeholder="Till exempel huvudkontoret" required />
            </div>
            <div className="my-company-field">
              <div className="my-company-label-row"><label htmlFor="my-company-area">Område</label><span>Valfritt</span></div>
              <input id="my-company-area" name="area" value={form.area} onChange={change} placeholder="Ort eller arbetsplats" />
            </div>
            <div className="my-company-field">
              <div className="my-company-label-row"><label htmlFor="my-company-description">Beskrivning</label><span>Valfritt</span></div>
              <textarea id="my-company-description" name="description" value={form.description} onChange={change} rows={4} placeholder="En kort beskrivning av projektet..." />
            </div>
          </fieldset>
          <footer className="my-company-actions">
            <div className="my-company-feedback">
              {error ? <p role="alert" className="my-company-error">{error}</p>
                : saved ? <p role="status" className="my-company-success">Projektet har sparats.</p>
                : null}
            </div>
            <button type="submit" disabled={saving}><Save size={16} aria-hidden="true" />{saving ? "Sparar..." : "Spara"}</button>
          </footer>
        </form>
      </div>
    </div>
  );
}

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("MyCompanyPage", `
.my-company-page { max-width: 1120px; padding: 8px 0 40px; color: #252b42; }
.my-company-context { margin: 0 0 24px; color: #6b7280; font-size: 14px; }
.my-company-heading { display: flex; align-items: center; gap: 16px; margin-bottom: 32px; }
.my-company-icon { display: grid; place-items: center; flex-shrink: 0; width: 54px; height: 54px; background: #eee8fc; color: #6323ef; border-radius: 16px; }
.my-company-heading h1 { font-size: 27px; letter-spacing: -.6px; margin: 0 0 6px; }
.my-company-heading p { margin: 0; color: #6b7280; font-size: 14px; line-height: 1.5; }
.my-company-layout { max-width: 740px; }
.my-company-card { min-width: 0; background: #fff; border: 1px solid #e7e9f1; border-radius: 16px; box-shadow: 0 4px 20px #252b4205; overflow: hidden; }
.my-company-card-heading { padding: 28px 30px 0; }
.my-company-card-heading h2 { font-size: 18px; margin: 0 0 8px; }
.my-company-card-heading p { font-size: 13px; color: #6b7280; line-height: 1.6; margin: 0; }
.my-company-fields { display: grid; gap: 24px; min-width: 0; padding: 28px 30px 32px; margin: 0; border: 0; }
.my-company-field { display: flex; flex-direction: column; gap: 9px; min-width: 0; }
.my-company-field label { font-size: 13px; font-weight: 600; }
.my-company-label-row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.my-company-label-row span { font-size: 11px; color: #737d94; }
.my-company-fields input, .my-company-fields textarea { box-sizing: border-box; width: 100%; border: 1px solid #dfe2ec; border-radius: 8px; padding: 13px 14px; font: inherit; font-size: 14px; color: #252b42; background: #fcfcfe; }
.my-company-fields textarea { resize: vertical; min-height: 110px; line-height: 1.6; }
.my-company-fields input::placeholder, .my-company-fields textarea::placeholder { color: #737d94; }
.my-company-fields input:focus, .my-company-fields textarea:focus { outline: 2px solid #6323ef; outline-offset: 2px; border-color: #6323ef; background: #fff; }
.my-company-actions { display: flex; align-items: center; justify-content: space-between; gap: 20px; border-top: 1px solid #ececf4; padding: 20px 30px; background: #fcfcfe; }
.my-company-feedback { min-width: 0; font-size: 12px; color: #737d94; overflow-wrap: anywhere; }
.my-company-feedback p { margin: 0; line-height: 1.5; }
.my-company-success { color: #16805b; }
.my-company-error { color: #b91c1c; }
.my-company-actions button { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; gap: 8px; border: 0; border-radius: 8px; background: #6323ef; color: #fff; padding: 12px 24px; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.my-company-actions button:hover:not(:disabled) { background: #5120c5; }
.my-company-actions button:focus-visible { outline: 2px solid #6323ef; outline-offset: 3px; }
.my-company-actions button:disabled { opacity: .6; cursor: wait; }
@media (max-width: 480px) {
  .my-company-heading { align-items: flex-start; gap: 12px; }
  .my-company-heading h1 { font-size: 23px; }
  .my-company-card-heading { padding: 22px 20px 0; }
  .my-company-fields { padding: 24px 20px; }
  .my-company-actions { padding: 18px 20px; flex-wrap: wrap; }
  .my-company-actions button { width: 100%; }
}
`);
